import { queryOptions } from '@tanstack/react-query';
import type { SlotListFilter, SlotReservesFilter } from '../lib/filters';
import { slotApi, type SlotId } from './slot-api';

/** Query-key factory; the slot mutations invalidate through `lists()`. */
export const slotKeys = {
  all: ['bonus-slots'] as const,
  lists: () => [...slotKeys.all, 'list'] as const,
  list: (filter: SlotListFilter) => [...slotKeys.lists(), filter] as const,
  details: () => [...slotKeys.all, 'detail'] as const,
  detail: (id: SlotId) => [...slotKeys.details(), String(id)] as const,
  reserves: (filter: SlotReservesFilter) =>
    [...slotKeys.all, 'reserves', filter] as const,
  equipments: () => [...slotKeys.all, 'equipments'] as const,
};

const OPTIONS_STALE_MS = 5 * 60 * 1000;

export const slotQueries = {
  list: (filter: SlotListFilter) =>
    queryOptions({
      queryKey: slotKeys.list(filter),
      queryFn: () => slotApi.list(filter),
      // Keep the previous page on screen while the next one loads.
      placeholderData: (prev) => prev,
    }),

  /**
   * The edit form seeds once per mount from this record, so it must never be
   * served from a previous open: dropped as soon as the dialog unmounts.
   */
  detail: (id: SlotId, enabled: boolean) =>
    queryOptions({
      queryKey: slotKeys.detail(id),
      queryFn: () => slotApi.get(id),
      enabled,
      gcTime: 0,
    }),

  reserves: (filter: SlotReservesFilter) =>
    queryOptions({
      queryKey: slotKeys.reserves(filter),
      queryFn: () => slotApi.reserves(filter),
      placeholderData: (prev) => prev,
    }),

  equipments: (enabled: boolean) =>
    queryOptions({
      queryKey: slotKeys.equipments(),
      queryFn: () => slotApi.equipments(),
      enabled,
      staleTime: OPTIONS_STALE_MS,
    }),
};
